import $script from 'scriptjs';
import UA from '@ali/tbm-ua';
import { sendOther } from '@ali/tbm-log';
import { PerformanceLog } from '@ali/tbm-mini-perf';
import { DEFAULT_ARMS_CONFIG, ARMS_SCRIPT } from './constants';
import {
  getUserNick,
  getUserId,
  getReleaseVersion,
  getPage,
  getChannel,
  getEnv,
  getAccountType,
} from './utils';

window.__bl ||
  (window.__bl = {
    config: {},
    pipe: [],
  });

let larInstance = null;

/**
 * Arms上报
 * @param apiName 上报名
 * @param params 上报参数
 */
export const armsReport = function (apiName, ...params) {
  try {
    if (window.__bl && window.__bl[apiName]) {
      window.__bl[apiName](...params);
    } else {
      window.__bl.pipe.push([apiName, ...params]);
    }
  } catch (e) {
    console.log('armsReport error:', e);
  }
};

export const performanceAnalysis = new PerformanceLog({
  useAutoIndex: true,
  sendGoldLog: ({ logKey, logData } = {}) => {
    sendOther(logKey, logData);
  },
  logData: {
    os: UA.isIOS ? 'ios' : UA.isAndroid ? 'android' : 'unknown',
  },
});

/**
 * 将性能数据上报到arms平台
 * https://help.aliyun.com/document_detail/58657.html?spm=a2c4g.58655.0.0#section-zjg-6v7-3w3
 */
function sendPerfReportToArms() {
  // 防止重复绑定
  if (window.__arms_perf_report_sent) {
    return;
  }
  window.__arms_perf_report_sent = 1;

  window.addEventListener('load', () => {
    setTimeout(() => {
      const { fcp, fmp, flt, nav, ttfb } =
        performanceAnalysis?.sendStatusMap ?? {};

      armsReport('performance', { cfpt: fcp, ctti: fmp, t1: flt });

      larInstance &&
        larInstance.performance({
          cfpt: { desc: 'fcp', cost: fcp },
          ctti: { desc: 'fmp', cost: fmp },
          t1: { desc: 'flt', cost: flt },
          t2: { desc: 'nav', cost: nav },
          t3: { desc: 'ttfb', cost: ttfb },
        });
    }, 1000);
  });
}

/** 钢铁侠模块，负责初始化Arms */
export class IronMan {
  config: any;
  errors: any;
  reportedErrorPv: boolean;

  constructor(config = {}) {
    this.reportedErrorPv = false;
    this.errors = [];
    this.config = config;
    this.initConfig();
    this.initErrorListener();
    sendPerfReportToArms();
    this.loadSDK();
  }

  initConfig() {
    // 1、由于userId可能存在httponly无法获取，查询可用usernick兜底
    // 2、域外小程序登录态标识用户字段暂无，要从共享的userId取
    // 3、大麦也是Havana账号，站点18，使用havanaId作为用户标识
    const userId = getUserId();
    const uid = userId;
    const channel = getChannel();
    const environment = getEnv();
    const release = getReleaseVersion();
    const page = getPage();
    const account = getAccountType();
    const applyConfig = {
      ...DEFAULT_ARMS_CONFIG,
      uid,
      setUsername: () => {
        this.reportErrors();
        return getUserNick();
      },
      tag: +new Date(),
      page,
      release,
      environment,
      autoSendPerf: false,
      c1: account,
      c2: channel,
      ...window.__armsConfig,
      ...this.config,
    };

    window.__bl.config = applyConfig;
  }

  getReportFlag(e) {
    let flag = false;
    const errorMsg = e?.message || e?.error?.message || e?.reason?.message;

    if (errorMsg) {
      flag = true;

      DEFAULT_ARMS_CONFIG.ignore.ignoreErrors.forEach((i: string) => {
        if (errorMsg?.indexOf(i) > -1) flag = false;
      });
    }

    return flag;
  }

  reportErrors() {
    if (this.errors === null || this.errors?.length === 0) return;
    const errorList = this.errors;
    this.errors = null;
    errorList?.forEach(e => {
      if (this.getReportFlag(e)) {
        armsReport('error', e);
      }
    });
  }

  cacheErrorHandle(e) {
    if (this.reportedErrorPv === true && this.errors === null) {
      this.removeErrorEventListener();
      return;
    }

    if (this.reportedErrorPv === false && this.getReportFlag(e)) {
      this.reportedErrorPv = true;
      armsReport('sum', 'error_pv');
    }

    if (this.errors !== null) {
      // 兼容 unhandledrejection 事件
      const error = e.reason ? e.reason : e;
      const target = error.target || error.srcElement;

      // 资源错误
      const isResource = !(
        !(target && target.tagName) ||
        error.message ||
        error.filename ||
        error.lineno ||
        error.colno
      );

      if (!isResource) this?.errors?.push(error);
    }
  }

  removeErrorEventListener() {
    window.removeEventListener('error', this.cacheErrorHandle.bind(this));
    window.removeEventListener(
      'unhandledrejection',
      this.cacheErrorHandle.bind(this)
    );
  }

  /**
   * 1、上报错误PV(一次PV，发生报错，只上报一次)
   * 2、补偿异步加载arms sdk期间的js错误
   */
  initErrorListener() {
    window.addEventListener('error', this.cacheErrorHandle.bind(this));
    window.addEventListener(
      'unhandledrejection',
      this.cacheErrorHandle.bind(this)
    );
  }

  loadSDK() {
    $script(ARMS_SCRIPT, () => {
      console.log('load arms script');
    });
  }
}

/** 初始化Arms */
export function initArms() {
  return new IronMan();
}
